import React, { useState, useRef } from "react";
import { Tabs, Tab, Table, Badge, Alert, Button, Modal } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFilePdf, FaFileExcel, FaExpand } from "react-icons/fa";
import CustomModal from "../../../core/common/customModal";
import PreLoader from "../../../core/common/preLoader";

interface RowResult {
  id?: number;
  entityId?: number;
  rowNumber: number;
  entityType: string;
  success: boolean;
  message?: string;
  errorMessage?: string;
  rowData: Record<string, any>;
}

interface BulkUploadResult {
  sheetName: string;
  successCount: number;
  failureCount: number;
  totalRows: number;
  rowResults: RowResult[];
}

interface BulkUploadResultsProps {
  results: BulkUploadResult | BulkUploadResult[];
  isValidation: boolean;
  inProgress: boolean;
  onSubmit?: () => void;
  onReset?: () => void;
}

const BulkUploadResults: React.FC<BulkUploadResultsProps> = ({
  results,
  isValidation,
  inProgress,
  onSubmit,
  onReset,
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    Array.isArray(results) && results.length > 0
      ? results[0].sheetName
      : !Array.isArray(results)
      ? results.sheetName
      : "summary"
  );
  const [showFullScreen, setShowFullScreen] = useState<boolean>(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Function to determine text color based on message status
  const getMessageTextColor = (row: RowResult) => {
    if (!row.success) {
      return "text-danger"; // Bootstrap class for red text
    }
    return ""; // Default text color
  };

  if (!results) {
    return <Alert variant="info">No results to display</Alert>;
  }

  // Convert to array if single result
  const resultsArray: BulkUploadResult[] = Array.isArray(results)
    ? results
    : [results];

  // Format summary stats
  const totalSuccess = resultsArray.reduce(
    (acc, result) => acc + result.successCount,
    0
  );
  const totalFailure = resultsArray.reduce(
    (acc, result) => acc + result.failureCount,
    0
  );
  const totalCount = resultsArray.reduce(
    (acc, result) => acc + result.totalRows,
    0
  );

  // Check if validation was successful (no failures in any sheet)
  const isValidationSuccessful = () => {
    return resultsArray.every((result) => result.failureCount === 0);
  };

  // Get column headers from row data (combine all unique keys)
  const getColumnHeaders = (rows: RowResult[]): string[] => {
    const allKeys = new Set<string>();
    rows.forEach((row) => {
      Object.keys(row.rowData || {}).forEach((key) => allKeys.add(key));
    });
    return Array.from(allKeys);
  };

  // Export to PDF functionality
  const exportToPDF = () => {
    // Create document in landscape orientation for better handling of wide tables
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a3", // Using A3 format for more space
    });

    // Get page dimensions for better space utilization
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20; // Smaller margins to use more space
    const usableWidth = pageWidth - 2 * margin;

    let currentY = margin + 10;

    // Add title and current date
    const title = `${
      isValidation ? "Validation" : "Upload"
    } Results - ${new Date().toLocaleString()}`;
    doc.setFontSize(16);
    doc.text(title, margin, currentY);
    currentY += 30;

    // Add summary
    doc.setFontSize(12);
    doc.text(
      `Total: ${totalCount} | Success: ${totalSuccess} | Failed: ${totalFailure}`,
      margin,
      currentY
    );
    currentY += 30;

    // Add summary table first
    doc.setFontSize(14);
    doc.text("Summary", margin, currentY);
    currentY += 20;

    const summaryData = resultsArray.map((result) => [
      result.sheetName,
      result.totalRows.toString(),
      result.successCount.toString(),
      result.failureCount.toString(),
    ]);

    autoTable(doc, {
      head: [["Sheet", "Total Records", "Success", "Failure"]],
      body: summaryData,
      startY: currentY,
      styles: {
        fontSize: 10,
        cellPadding: 5,
      },
      headStyles: {
        fillColor: [41, 128, 185] as [number, number, number],
        textColor: [255, 255, 255] as [number, number, number],
        fontStyle: "bold" as "bold",
      },
      margin: { left: margin, right: margin },
      tableWidth: usableWidth,
    });

    // Update current position after drawing the table
    currentY = (doc as any).lastAutoTable.finalY + 40;

    // Create tables for each sheet
    for (const result of resultsArray) {
      // Start a new page for each sheet
      doc.addPage("a3");
      currentY = margin + 10;

      // Add sheet name as a heading
      doc.setFontSize(14);
      doc.text(`Sheet: ${result.sheetName}`, margin, currentY);
      currentY += 20;

      if (result.rowResults.length === 0) {
        doc.setFontSize(10);
        doc.text("No records found in this sheet", margin, currentY);
        continue;
      }

      const columnHeaders = getColumnHeaders(result.rowResults);
      const tableHeaders = [
        "Row",
        "Status",
        ...columnHeaders,
        !isValidation ? "Entity ID" : "",
        "Message",
      ].filter(Boolean);

      // Format row data
      const tableData = result.rowResults.map((row) => {
        const rowData = [
          row.rowNumber.toString(),
          row.success ? "Success" : "Error",
        ];

        // Add column data
        columnHeaders.forEach((header) => {
          rowData.push(
            row.rowData[header] !== undefined ? String(row.rowData[header]) : ""
          );
        });

        // Add entity ID for non-validation results
        if (!isValidation) {
          rowData.push(row.entityId ? String(row.entityId) : "-");
        }

        // Add message
        const messages = [
          ...(row.errorMessage?.split(";") || []),
          ...(row.message?.split(";") || []),
        ]
          .filter((msg) => msg.trim())
          .map((msg, idx) => `${idx + 1}. ${msg.trim()}`)
          .join("\n");

        rowData.push(messages || (row.success ? "Success" : "Unknown error"));

        return rowData;
      });

      // Prepare column width configurations
      const columnStyles: Record<number, any> = {};

      // Column width distribution based on content importance:
      // - We'll allocate fixed widths for utility columns
      // - Distribute remaining width among data columns

      // Fixed width columns
      columnStyles[0] = { cellWidth: 30 }; // Row number (narrower)
      columnStyles[1] = { cellWidth: 40 }; // Status (narrower)

      // Message column (last column) - allocate more space
      const messageColIndex = tableHeaders.length - 1;
      const messageColWidth = Math.max(150, usableWidth * 0.18); // At least 18% of usable width
      columnStyles[messageColIndex] = {
        cellWidth: messageColWidth,
        halign: "left" as "left",
        overflow: "linebreak" as "linebreak",
      };

      // Calculate space for data columns
      const fixedWidth = 30 + 40 + messageColWidth; // Row + Status + Message
      const dataColumnsCount = tableHeaders.length - 3; // Subtract Row, Status, and Message

      if (dataColumnsCount > 0) {
        // Distribute remaining width among data columns, with minimum width
        const remainingWidth = usableWidth - fixedWidth;
        const dataColumnWidth = Math.max(50, remainingWidth / dataColumnsCount);

        // For sheets with many columns, adjust based on column count
        for (let i = 2; i < messageColIndex; i++) {
          columnStyles[i] = {
            cellWidth: dataColumnWidth,
            overflow: "linebreak" as "linebreak",
            halign: "left" as "left",
          };
        }
      }

      // Configure autotable options with improved settings
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: currentY,
        styles: {
          fontSize: 9,
          cellPadding: 4,
          lineColor: [80, 80, 80] as [number, number, number],
          lineWidth: 0.1,
          minCellHeight: 12,
          valign: "middle" as "middle",
          overflow: "linebreak" as "linebreak",
          textColor: [0, 0, 0] as [number, number, number], // Always black text
          halign: "left" as "left", // Default left alignment for better readability
        },
        headStyles: {
          fillColor: [41, 128, 185] as [number, number, number],
          textColor: [255, 255, 255] as [number, number, number],
          fontSize: 10,
          fontStyle: "bold" as "bold",
          halign: "center" as "center",
        },
        theme: "grid" as "grid",
        showHead: "everyPage" as "everyPage",
        columnStyles: columnStyles,
        margin: { left: margin, right: margin },
        tableWidth: usableWidth,
        // Apply cell styling using didParseCell instead of cellStyles property
        didParseCell: function (data) {
          const rowIndex = data.row.index;

          // Only apply to body cells
          if (data.section === "body" && rowIndex !== undefined) {
            // Check if this is an error row
            const isError =
              tableData[rowIndex] && tableData[rowIndex][1] === "Error";

            // Apply the appropriate styling
            if (isError) {
              data.cell.styles.fillColor = [255, 235, 235];
            } else {
              data.cell.styles.fillColor =
                rowIndex % 2 === 0 ? [255, 255, 255] : [245, 245, 245];
            }

            // Ensure text is always black for readability
            data.cell.styles.textColor = [0, 0, 0];
          }
        },
        didDrawPage: (data: any) => {
          // Add footer with page numbers
          // Use internal property for page count in a type-safe way
          const pageCount = (doc as any).internal.pages.length - 1;
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount} - ${result.sheetName}`,
            pageWidth - 120,
            pageHeight - margin
          );
        },
      });
    }

    doc.save(
      `bulk-${isValidation ? "validation" : "upload"}-all-sheets-${
        new Date().toISOString().split("T")[0]
      }.pdf`
    );
  };

  // Export to Excel functionality
  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Add summary sheet
    const summaryData = [
      ["Sheet", "Total Records", "Success", "Failure"],
      ...resultsArray.map((result) => [
        result.sheetName,
        result.totalRows,
        result.successCount,
        result.failureCount,
      ]),
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Add sheet for each tab
    resultsArray.forEach((result) => {
      const columnHeaders = getColumnHeaders(result.rowResults);
      const tableHeaders = [
        "Row",
        "Status",
        ...columnHeaders,
        !isValidation ? "Entity ID" : null,
        "Message",
      ].filter(Boolean);

      const tableData = result.rowResults.map((row) => {
        const rowData: any[] = [
          row.rowNumber,
          row.success ? "Success" : "Error",
        ];

        // Add column data
        columnHeaders.forEach((header) => {
          rowData.push(
            row.rowData[header] !== undefined ? row.rowData[header] : ""
          );
        });

        // Add entity ID for non-validation results
        if (!isValidation) {
          rowData.push(row.entityId || "-");
        }

        // Add message
        const messages = [
          ...(row.errorMessage?.split(";") || []),
          ...(row.message?.split(";") || []),
        ]
          .filter((msg) => msg.trim())
          .map((msg, idx) => `${idx + 1}. ${msg.trim()}`)
          .join("\n");

        rowData.push(messages || (row.success ? "Success" : "Unknown error"));

        return rowData;
      });

      const sheetData = [tableHeaders, ...tableData];
      const sheet = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, sheet, result.sheetName);
    });

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      data,
      `bulk-${isValidation ? "validation" : "upload"}-results-${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
  };

  const resultsContent = (
    <div className="bulk-upload-results" ref={resultsRef}>
      <div className="d-flex flex-wrap gap-1 justify-content-between align-items-center mb-3">
        <Alert
          variant={isValidation ? "warning" : "success"}
          className="mb-0 flex-grow-1 me-2"
        >
          <h5>{isValidation ? "Validation Results" : "Upload Complete"}</h5>
          <p className="mb-0">
            {isValidation
              ? `Validation found ${totalSuccess} valid records and ${totalFailure} invalid records out of ${totalCount} total records.`
              : `Successfully processed ${totalSuccess} records out of ${totalCount} total records.`}
          </p>
        </Alert>

        <div className="d-flex">
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={() => setShowFullScreen(true)}
            title="View in full screen"
          >
            <FaExpand />
          </Button>
          <Button
            variant="outline-danger"
            className="me-2"
            onClick={exportToPDF}
            title="Download as PDF"
          >
            <FaFilePdf />
          </Button>
          <Button
            variant="outline-success"
            onClick={exportToExcel}
            title="Download as Excel"
          >
            <FaFileExcel />
          </Button>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || "summary")}
        className="mb-3"
      >
        <Tab eventKey="summary" title="Summary">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Sheet</th>
                <th>Total Records</th>
                <th>Success</th>
                <th>Failure</th>
              </tr>
            </thead>
            <tbody>
              {resultsArray.map((result, index) => (
                <tr key={index}>
                  <td>{result.sheetName}</td>
                  <td>{result.totalRows}</td>
                  <td>
                    <Badge bg="success">
                      {result.successCount} (
                      {result.totalRows > 0
                        ? Math.round(
                            (result.successCount / result.totalRows) * 100
                          )
                        : 0}
                      %)
                    </Badge>
                  </td>
                  <td>
                    <Badge bg="danger">
                      {result.failureCount} (
                      {result.totalRows > 0
                        ? Math.round(
                            (result.failureCount / result.totalRows) * 100
                          )
                        : 0}
                      %)
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        {resultsArray.map((result, index) => {
          const columnHeaders = getColumnHeaders(result.rowResults);

          return (
            <Tab
              key={index}
              eventKey={result.sheetName}
              title={result.sheetName}
            >
              {result.rowResults.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Status</th>
                        {columnHeaders.map((header, idx) => (
                          <th key={idx}>{header}</th>
                        ))}
                        {!isValidation && <th>Entity ID</th>}
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rowResults.map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className={row.success ? "" : "table-danger"}
                        >
                          <td>{row.rowNumber}</td>
                          <td>
                            <Badge bg={row.success ? "success" : "danger"}>
                              {row.success ? "Success" : "Error"}
                            </Badge>
                          </td>
                          {columnHeaders.map((header, colIdx) => (
                            <td key={colIdx}>
                              {row.rowData[header] !== undefined
                                ? String(row.rowData[header])
                                : ""}
                            </td>
                          ))}
                          {!isValidation && <td>{row.entityId || "-"}</td>}
                          <td className={getMessageTextColor(row)}>
                            {row.errorMessage || row.message ? (
                              <div>
                                {/* Combine messages from both fields and split by semicolon */}
                                {[
                                  ...(row.errorMessage?.split(";") || []),
                                  ...(row.message?.split(";") || []),
                                ]
                                  .filter((message) => message.trim()) // Filter out empty messages
                                  .map((message, msgIdx) => (
                                    <div key={msgIdx}>
                                      {msgIdx + 1}. {message.trim()}
                                    </div>
                                  ))}
                              </div>
                            ) : row.success ? (
                              "Success"
                            ) : (
                              "Unknown error"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info">No records found in this sheet</Alert>
              )}
            </Tab>
          );
        })}
      </Tabs>

      <CustomModal 
        show={showFullScreen}
        onClose={() => setShowFullScreen(false)}
        dialogClassName="modal-fullscreen"
        title={<>
            {isValidation ? "Validation Results" : "Upload Results"}
            <span className="text-muted ms-2 small">
              {activeTab === "summary" ? "Summary" : `Sheet: ${activeTab}`}
            </span>
            <div className="text-muted small mt-1">
              {isValidation
                ? `Validation found ${totalSuccess} valid records and ${totalFailure} invalid records out of ${totalCount} total records.`
                : `Successfully processed ${totalSuccess} records out of ${totalCount} total records.`}
            </div>
        </>}
        bodyContent={(
        <>
          <div className="d-flex justify-content-end mb-3">
            <PreLoader showLoader={inProgress} />
            <Button
              variant="outline-danger"
              className="me-2"
              onClick={exportToPDF}
              title="Download as PDF"
            >
              <FaFilePdf className="me-1" /> Export as PDF
            </Button>
            <Button
              variant="outline-success"
              onClick={exportToExcel}
              title="Download as Excel"
            >
              <FaFileExcel className="me-1" /> Export as Excel
            </Button>
          </div>

          <Tabs
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key || "summary")}
            className="mb-3"
          >
            <Tab eventKey="summary" title="Summary">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Sheet</th>
                    <th>Total Records</th>
                    <th>Success</th>
                    <th>Failure</th>
                  </tr>
                </thead>
                <tbody>
                  {resultsArray.map((result, index) => (
                    <tr key={index}>
                      <td>{result.sheetName}</td>
                      <td>{result.totalRows}</td>
                      <td>
                        <Badge bg="success">
                          {result.successCount} (
                          {result.totalRows > 0
                            ? Math.round(
                                (result.successCount / result.totalRows) * 100
                              )
                            : 0}
                          %)
                        </Badge>
                      </td>
                      <td>
                        <Badge bg="danger">
                          {result.failureCount} (
                          {result.totalRows > 0
                            ? Math.round(
                                (result.failureCount / result.totalRows) * 100
                              )
                            : 0}
                          %)
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>

            {resultsArray.map((result, index) => {
              const columnHeaders = getColumnHeaders(result.rowResults);

              return (
                <Tab
                  key={index}
                  eventKey={result.sheetName}
                  title={result.sheetName}
                >
                  {result.rowResults.length > 0 ? (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Status</th>
                            {columnHeaders.map((header, idx) => (
                              <th key={idx}>{header}</th>
                            ))}
                            {!isValidation && <th>Entity ID</th>}
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.rowResults.map((row, rowIdx) => (
                            <tr
                              key={rowIdx}
                              className={row.success ? "" : "table-danger"}
                            >
                              <td>{row.rowNumber}</td>
                              <td>
                                <Badge bg={row.success ? "success" : "danger"}>
                                  {row.success ? "Success" : "Error"}
                                </Badge>
                              </td>
                              {columnHeaders.map((header, colIdx) => (
                                <td key={colIdx}>
                                  {row.rowData[header] !== undefined
                                    ? String(row.rowData[header])
                                    : ""}
                                </td>
                              ))}
                              {!isValidation && <td>{row.entityId || "-"}</td>}
                              <td className={getMessageTextColor(row)}>
                                {row.errorMessage || row.message ? (
                                  <div>
                                    {/* Combine messages from both fields and split by semicolon */}
                                    {[
                                      ...(row.errorMessage?.split(";") || []),
                                      ...(row.message?.split(";") || []),
                                    ]
                                      .filter((message) => message.trim()) // Filter out empty messages
                                      .map((message, msgIdx) => (
                                        <div key={msgIdx}>
                                          {msgIdx + 1}. {message.trim()}
                                        </div>
                                      ))}
                                  </div>
                                ) : row.success ? (
                                  "Success"
                                ) : (
                                  "Unknown error"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant="info">No records found in this sheet</Alert>
                  )}
                </Tab>
              );
            })}
          </Tabs>
        </>
        )}
        footerContent={          
          <div className="d-flex w-100 justify-content-end">
            {isValidation && isValidationSuccessful() && onSubmit && (
              <Button
                variant="primary"
                onClick={onSubmit}
                style={{ marginRight: "10px" }}
              >
                Submit
              </Button>
            )}
            {onReset && (
              <Button
                variant="secondary"
                onClick={onReset}
                style={{ marginRight: "10px" }}
              >
                {!isValidation ? "Upload Another" : "Select Another"}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setShowFullScreen(false)}
            >
              Close
            </Button>
          </div>
        }/>
    </div>
  );

  return resultsContent;
};

export default BulkUploadResults;
